import signalRService, { SignalRNotificationMessage } from '@/services/signalR/SignalRService';
import { useEffect, useState, useCallback, useRef } from 'react';
import * as signalR from '@microsoft/signalr';

interface UseSignalROptions {
    onConnectionStateChanged?: (isConnected: boolean) => void;
    // Event handlers - chỉ các event có trong setupEventHandlers
    onReloadEmployee?: (notification: SignalRNotificationMessage) => void;
    onReloadDepartment?: (notification: SignalRNotificationMessage) => void;
    onReloadRoleGroup?: (notification: SignalRNotificationMessage) => void;
    onReloadPeriod?: (notification: SignalRNotificationMessage) => void;
    onReloadUnit?: (notification: SignalRNotificationMessage) => void;
    onMessage?: (fromUserId: string, message: string) => void;
    onGroupMessage?: (groupName: string, fromUserId: string, message: string) => void;
    onNotification?: (notification: SignalRNotificationMessage) => void;
    onSystemMessage?: (message: string) => void;
    onUserDisconnected?: (data: { userId: string; timestamp: string }) => void;
    onError?: (errorMessage: string) => void;
    // Thread/Comment event handlers - nhận SignalRNotificationMessage giống ReceiveNotification
    onThreadCreated?: (notification: SignalRNotificationMessage) => void;
    onThreadUpdated?: (notification: SignalRNotificationMessage) => void;
    onThreadDeleted?: (notification: SignalRNotificationMessage) => void;
    onThreadReactionUpdated?: (notification: SignalRNotificationMessage) => void;
    onCommentCreated?: (notification: SignalRNotificationMessage) => void;
    onCommentUpdated?: (notification: SignalRNotificationMessage) => void;
    onCommentDeleted?: (notification: SignalRNotificationMessage) => void;
    onCommentReactionUpdated?: (notification: SignalRNotificationMessage) => void;
    // ThreadPage event handlers
    onThreadPageCreated?: (notification: SignalRNotificationMessage) => void;
    onThreadPageUpdated?: (notification: SignalRNotificationMessage) => void;
    onThreadPageDeleted?: (notification: SignalRNotificationMessage) => void;
    onThreadPageUserJoined?: (notification: SignalRNotificationMessage) => void;
    onThreadPageUserLeft?: (notification: SignalRNotificationMessage) => void;
    onThreadPageRequestApproved?: (notification: SignalRNotificationMessage) => void;
    onThreadPageRequestRejected?: (notification: SignalRNotificationMessage) => void;
    onThreadPageOwnershipTransferred?: (notification: SignalRNotificationMessage) => void;
    // ThreadPoll event handlers
    onThreadPollCreated?: (notification: SignalRNotificationMessage) => void;
    onThreadPollUpdated?: (notification: SignalRNotificationMessage) => void;
    onThreadPollDeleted?: (notification: SignalRNotificationMessage) => void;
    onThreadPollVoted?: (notification: SignalRNotificationMessage) => void;
    autoConnect?: boolean;
}

export const useSignalR = (options: UseSignalROptions = {}) => {
    const {
        onConnectionStateChanged,
        onReloadEmployee,
        onReloadDepartment,
        onReloadRoleGroup,
        onReloadPeriod,
        onReloadUnit,
        onMessage,
        onGroupMessage,
        onNotification,
        onSystemMessage,
        onUserDisconnected,
        onError,
        onThreadCreated,
        onThreadUpdated,
        onThreadDeleted,
        onThreadReactionUpdated,
        onCommentCreated,
        onCommentUpdated,
        onCommentDeleted,
        onCommentReactionUpdated,
        onThreadPageCreated,
        onThreadPageUpdated,
        onThreadPageDeleted,
        onThreadPageUserJoined,
        onThreadPageUserLeft,
        onThreadPageRequestApproved,
        onThreadPageRequestRejected,
        onThreadPageOwnershipTransferred,
        onThreadPollCreated,
        onThreadPollUpdated,
        onThreadPollDeleted,
        onThreadPollVoted,
        autoConnect = false
    } = options;

    // State để track connection
    const [connectionId, setConnectionId] = useState<string | null>(null);
    const [isConnected, setIsConnected] = useState<boolean>(false);
    const [connectionState, setConnectionState] = useState<signalR.HubConnectionState | null>(null);
    
    // Sử dụng ref để tránh re-register handlers khi callbacks thay đổi
    const handlersRef = useRef(options);
    handlersRef.current = options;

    // Internal connection state change handler để update local state
    const handleConnectionStateChanged = useCallback((connected: boolean) => {
        setIsConnected(connected);
        setConnectionState(signalRService.getConnectionState());
        setConnectionId(signalRService.getConnectionId());
        
        // Gọi user-provided callback nếu có
        if (handlersRef.current.onConnectionStateChanged) {
            handlersRef.current.onConnectionStateChanged(connected);
        }
    }, []);

    // Set up event handlers - chỉ register một lần
    useEffect(() => {
        const opts = handlersRef.current;
        
        // Luôn register internal connection state handler
        signalRService.setOnConnectionStateChanged(handleConnectionStateChanged);
        
        // Register các handlers khác nếu có
        if (opts.onReloadEmployee) {
            signalRService.setOnReloadEmployee(opts.onReloadEmployee);
        }
        if (opts.onReloadDepartment) {
            signalRService.setOnReloadDepartment(opts.onReloadDepartment);
        }
        if (opts.onReloadRoleGroup) {
            signalRService.setOnReloadRoleGroup(opts.onReloadRoleGroup);
        }
        if (opts.onReloadPeriod) {
            signalRService.setOnReloadPeriod(opts.onReloadPeriod);
        }
        if (opts.onReloadUnit) {
            signalRService.setOnReloadUnit(opts.onReloadUnit);
        }
        if (opts.onMessage) {
            signalRService.setOnMessage(opts.onMessage);
        }
        if (opts.onGroupMessage) {
            signalRService.setOnGroupMessage(opts.onGroupMessage);
        }
        if (opts.onNotification) {
            signalRService.setOnNotification(opts.onNotification);
        }
        if (opts.onSystemMessage) {
            signalRService.setOnSystemMessage(opts.onSystemMessage);
        }
        if (opts.onUserDisconnected) {
            signalRService.setOnUserDisconnected(opts.onUserDisconnected);
        }
        if (opts.onError) {
            signalRService.setOnError(opts.onError);
        }
        if (opts.onThreadCreated) {
            signalRService.setOnThreadCreated(opts.onThreadCreated);
        }
        if (opts.onThreadUpdated) {
            signalRService.setOnThreadUpdated(opts.onThreadUpdated);
        }
        if (opts.onThreadDeleted) {
            signalRService.setOnThreadDeleted(opts.onThreadDeleted);
        }
        if (opts.onThreadReactionUpdated) {
            signalRService.setOnThreadReactionUpdated(opts.onThreadReactionUpdated);
        }
        if (opts.onCommentCreated) {
            signalRService.setOnCommentCreated(opts.onCommentCreated);
        }
        if (opts.onCommentUpdated) {
            signalRService.setOnCommentUpdated(opts.onCommentUpdated);
        }
        if (opts.onCommentDeleted) {
            signalRService.setOnCommentDeleted(opts.onCommentDeleted);
        }
        if (opts.onCommentReactionUpdated) {
            signalRService.setOnCommentReactionUpdated(opts.onCommentReactionUpdated);
        }
        if (opts.onThreadPageCreated) {
            signalRService.setOnThreadPageCreated(opts.onThreadPageCreated);
        }
        if (opts.onThreadPageUpdated) {
            signalRService.setOnThreadPageUpdated(opts.onThreadPageUpdated);
        }
        if (opts.onThreadPageDeleted) {
            signalRService.setOnThreadPageDeleted(opts.onThreadPageDeleted);
        }
        if (opts.onThreadPageUserJoined) {
            signalRService.setOnThreadPageUserJoined(opts.onThreadPageUserJoined);
        }
        if (opts.onThreadPageUserLeft) {
            signalRService.setOnThreadPageUserLeft(opts.onThreadPageUserLeft);
        }
        if (opts.onThreadPageRequestApproved) {
            signalRService.setOnThreadPageRequestApproved(opts.onThreadPageRequestApproved);
        }
        if (opts.onThreadPageRequestRejected) {
            signalRService.setOnThreadPageRequestRejected(opts.onThreadPageRequestRejected);
        }
        if (opts.onThreadPageOwnershipTransferred) {
            signalRService.setOnThreadPageOwnershipTransferred(opts.onThreadPageOwnershipTransferred);
        }
        if (opts.onThreadPollCreated) {
            signalRService.setOnThreadPollCreated(opts.onThreadPollCreated);
        }
        if (opts.onThreadPollUpdated) {
            signalRService.setOnThreadPollUpdated(opts.onThreadPollUpdated);
        }
        if (opts.onThreadPollDeleted) {
            signalRService.setOnThreadPollDeleted(opts.onThreadPollDeleted);
        }
        if (opts.onThreadPollVoted) {
            signalRService.setOnThreadPollVoted(opts.onThreadPollVoted);
        }
        
        // Auto connect if enabled (default false — AppSignalRConnector owns lifecycle)
        if (autoConnect) {
            signalRService.connect().catch((err) => {
                console.error('Failed to auto-connect SignalR:', err);
            });
        }
        
        // Initial state update
        setIsConnected(signalRService.isConnectionActive());
        setConnectionState(signalRService.getConnectionState());
        setConnectionId(signalRService.getConnectionId());
        
        // Cleanup handlers on unmount
        return () => {
            signalRService.removeOnConnectionStateChanged(handleConnectionStateChanged);
            
            if (opts.onReloadEmployee) {
                signalRService.removeOnReloadEmployee(opts.onReloadEmployee);
            }
            if (opts.onReloadDepartment) {
                signalRService.removeOnReloadDepartment(opts.onReloadDepartment);
            }   
            if (opts.onReloadRoleGroup) {
                signalRService.removeOnReloadRoleGroup(opts.onReloadRoleGroup);
            }
            if (opts.onReloadPeriod) {
                signalRService.removeOnReloadPeriod(opts.onReloadPeriod);
            }
            if (opts.onReloadUnit) {
                signalRService.removeOnReloadUnit(opts.onReloadUnit);
            }
            if (opts.onMessage) {
                signalRService.removeOnMessage(opts.onMessage);
            }
            if (opts.onGroupMessage) {
                signalRService.removeOnGroupMessage(opts.onGroupMessage);
            }
            if (opts.onNotification) {
                signalRService.removeOnNotification(opts.onNotification);
            }
            if (opts.onSystemMessage) {
                signalRService.removeOnSystemMessage(opts.onSystemMessage);
            }
            if (opts.onUserDisconnected) {
                signalRService.removeOnUserDisconnected(opts.onUserDisconnected);
            }
            if (opts.onError) {
                signalRService.removeOnError(opts.onError);
            }
            if (opts.onThreadCreated) {
                signalRService.removeOnThreadCreated(opts.onThreadCreated);
            }
            if (opts.onThreadUpdated) {
                signalRService.removeOnThreadUpdated(opts.onThreadUpdated);
            }
            if (opts.onThreadDeleted) {
                signalRService.removeOnThreadDeleted(opts.onThreadDeleted);
            }
            if (opts.onThreadReactionUpdated) {
                signalRService.removeOnThreadReactionUpdated(opts.onThreadReactionUpdated);
            }
            if (opts.onCommentCreated) {
                signalRService.removeOnCommentCreated(opts.onCommentCreated);
            }
            if (opts.onCommentUpdated) {
                signalRService.removeOnCommentUpdated(opts.onCommentUpdated);
            }
            if (opts.onCommentDeleted) {
                signalRService.removeOnCommentDeleted(opts.onCommentDeleted);
            }
            if (opts.onCommentReactionUpdated) {
                signalRService.removeOnCommentReactionUpdated(opts.onCommentReactionUpdated);
            }
            if (opts.onThreadPageCreated) {
                signalRService.removeOnThreadPageCreated(opts.onThreadPageCreated);
            }
            if (opts.onThreadPageUpdated) {
                signalRService.removeOnThreadPageUpdated(opts.onThreadPageUpdated);
            }
            if (opts.onThreadPageDeleted) {
                signalRService.removeOnThreadPageDeleted(opts.onThreadPageDeleted);
            }
            if (opts.onThreadPageUserJoined) {
                signalRService.removeOnThreadPageUserJoined(opts.onThreadPageUserJoined);
            }
            if (opts.onThreadPageUserLeft) {
                signalRService.removeOnThreadPageUserLeft(opts.onThreadPageUserLeft);
            }
            if (opts.onThreadPageRequestApproved) {
                signalRService.removeOnThreadPageRequestApproved(opts.onThreadPageRequestApproved);
            }
            if (opts.onThreadPageRequestRejected) {
                signalRService.removeOnThreadPageRequestRejected(opts.onThreadPageRequestRejected);
            }
            if (opts.onThreadPageOwnershipTransferred) {
                signalRService.removeOnThreadPageOwnershipTransferred(opts.onThreadPageOwnershipTransferred);
            }
            if (opts.onThreadPollCreated) {
                signalRService.removeOnThreadPollCreated(opts.onThreadPollCreated);
            }
            if (opts.onThreadPollUpdated) {
                signalRService.removeOnThreadPollUpdated(opts.onThreadPollUpdated);
            }
            if (opts.onThreadPollDeleted) {
                signalRService.removeOnThreadPollDeleted(opts.onThreadPollDeleted);
            }
            if (opts.onThreadPollVoted) {
                signalRService.removeOnThreadPollVoted(opts.onThreadPollVoted);
            }
        };
    }, [handleConnectionStateChanged]); // Chỉ depend on handleConnectionStateChanged

    // Update connection state periodically để đảm bảo UI luôn sync
    useEffect(() => {
        const updateConnectionState = () => {
            const currentIsConnected = signalRService.isConnectionActive();
            const currentState = signalRService.getConnectionState();
            const currentId = signalRService.getConnectionId();
            
            // Chỉ update nếu có thay đổi để tránh re-render không cần thiết
            setIsConnected(prev => prev !== currentIsConnected ? currentIsConnected : prev);
            setConnectionState(prev => prev !== currentState ? currentState : prev);
            setConnectionId(prev => prev !== currentId ? currentId : prev);
        };

        // Initial update
        updateConnectionState();

        // Update mỗi 2 giây để catch connection changes (giảm từ 1s để tối ưu performance)
        const interval = setInterval(updateConnectionState, 2000);

        return () => clearInterval(interval);
    }, []);

    // Wrapper functions với error handling
    const connect = useCallback(async () => {
        try {
            await signalRService.connect();
            // Update state sau khi connect
            setIsConnected(signalRService.isConnectionActive());
            setConnectionState(signalRService.getConnectionState());
            setConnectionId(signalRService.getConnectionId());
        } catch (error) {
            console.error('Failed to connect SignalR:', error);
            throw error;
        }
    }, []);

    const disconnect = useCallback(async () => {
        try {
            await signalRService.disconnect();
            setIsConnected(false);
            setConnectionState(null);
            setConnectionId(null);
        } catch (error) {
            console.error('Failed to disconnect SignalR:', error);
            throw error;
        }
    }, []);

    const restart = useCallback(async () => {
        try {
            await signalRService.restart();
            setIsConnected(signalRService.isConnectionActive());
            setConnectionState(signalRService.getConnectionState());
            setConnectionId(signalRService.getConnectionId());
        } catch (error) {
            console.error('Failed to restart SignalR:', error);
            throw error;
        }
    }, []);

    return {
        connect,
        disconnect,
        restart,
        safeDisconnect: () => signalRService.safeDisconnect(),
        // Ticket group methods
        joinTicketGroup: (ticketId: string) => signalRService.joinTicketGroup(ticketId),
        leaveTicketGroup: (ticketId: string) => signalRService.leaveTicketGroup(ticketId),
        // ChatHub methods
        sendPrivateMessage: (toUserId: string, message: string) => signalRService.sendPrivateMessage(toUserId, message),
        joinGroup: (groupName: string) => signalRService.joinGroup(groupName),
        leaveGroup: (groupName: string) => signalRService.leaveGroup(groupName),
        sendMessageToGroup: (groupName: string, message: string) => signalRService.sendMessageToGroup(groupName, message),
        sendMessage: (message: string) => signalRService.sendMessage(message),
        sendEventToGroup: (groupName: string, eventName: string, data: any) => signalRService.sendEventToGroup(groupName, eventName, data),
        getOnlineUserConnections: () => signalRService.getOnlineUserConnections(),
        // Connection state - sử dụng state thay vì gọi trực tiếp để reactive
        isConnected,
        connectionState,
        connectionId
    };
};
